import React, { useState, useEffect } from 'react';
import SubmissionsDisplay from './SubmissionsDisplay';
import ImportSubmissionsModal from './ImportSubmissionsModal';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';
import { Search, Upload, Printer } from 'lucide-react';

// Mirrors EventSubmission.STATUS on the backend.
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PENDING_PAYMENT', label: 'Pending payment' },
  { value: 'PAYMENT_FAILED', label: 'Payment failed' },
];

const SubmissionList = () => {
  const { currentEvent, hasRole } = useAppState();
  const canImport = hasRole(['owner', 'admin', 'event_manager']);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportingBadges, setExportingBadges] = useState(false);
  
  // Search state - simplified to single search query
  const [searchQuery, setSearchQuery] = useState('');
  // Status filter - '' means all statuses
  const [statusFilter, setStatusFilter] = useState('');
  
  // Fetch submissions when the current event or the status filter changes
  useEffect(() => {
    if (currentEvent?.id) {
      fetchSubmissions();
    } else {
      setSubmissions([]);
    }
  }, [currentEvent, statusFilter]);
  
  // Build query params from the current search + status filter state.
  // `overrides` lets a caller replace a value that state hasn't caught up
  // with yet (e.g. clearing the search box).
  const buildParams = (overrides = {}) => {
    const params = {
      search: searchQuery.trim(),
      status: statusFilter,
      ...overrides,
    };
    // Drop empty values so the backend doesn't receive blank filters
    return Object.fromEntries(Object.entries(params).filter(([, v]) => v));
  };
  
  // Function to fetch submissions from API, always applying the active filters
  const fetchSubmissions = async (overrides = {}) => {
    if (!currentEvent?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/events/${currentEvent.id}/submissions`, { 
        params: buildParams(overrides)
      });
      setSubmissions(response.data);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to export submissions as CSV
  const exportSubmissions = async () => {
    if (!currentEvent?.id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/events/${currentEvent.id}/submissions/export/`, {
        responseType: 'blob', 
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      let filename = `submissions_${currentEvent.name}_${new Date().toISOString().slice(0, 10)}.csv`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error exporting submissions:', err);
      setError('Failed to export submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  
  // Download a zip of badge PDFs for every approved submission (for off-site printing).
  // Rendering hundreds of badges can take a while, so this request gets a
  // much longer timeout than the default and shows progress on its button
  // instead of replacing the whole page.
  const exportBadges = async () => {
    if (!currentEvent?.id || exportingBadges) return;

    setExportingBadges(true);
    try {
      const response = await api.get(`/events/${currentEvent.id}/submissions/badges/export/`, {
        responseType: 'blob',
        timeout: 10 * 60 * 1000,
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentEvent.slug || currentEvent.name}-badges.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const failed = Number(response.headers['x-badges-failed'] || 0);
      if (failed > 0) {
        alert(`${failed} badge(s) could not be rendered. See manifest.csv inside the zip for details.`);
      }
    } catch (err) {
      console.error('Error exporting badges:', err);
      if (err.response?.status === 404) {
        alert('This event has no approved submissions to export badges for.');
      } else {
        alert('Failed to export badges. Please try again.');
      }
    } finally {
      setExportingBadges(false);
    }
  };

  // Function to handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchSubmissions();
  };
  
  // Function to update submission status
  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      await api.put(`/events/${currentEvent.id}/submissions/${submissionId}/`, {
        status: newStatus
      });
      
      // Refresh submissions to get updated data
      await fetchSubmissions();
    } catch (err) {
      console.error('Error updating submission status:', err);
      alert('Failed to update submission status. Please try again.');
    }
  };

  // Handle successful import
  const handleImportSuccess = () => {
    fetchSubmissions(); // Refresh the submissions list
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <div className="animate-pulse">Loading submissions...</div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white shadow rounded-lg p-6 text-center text-red-600">
          {error}
          <button 
            onClick={() => fetchSubmissions()}
            className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  if (!currentEvent) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          Please select an event to view submissions.
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">{currentEvent.name} - Submissions</h1>
        <div className="flex gap-2">
          {canImport && (
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2"
            >
              <Upload size={16} />
              Import Submissions
            </button>
          )}
          <button
            onClick={() => exportSubmissions()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Export Submissions
          </button>
          <button
            onClick={() => exportBadges()}
            disabled={exportingBadges}
            title="Download a zip of badge PDFs for all approved submissions"
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Printer size={16} />
            {exportingBadges ? 'Preparing badges...' : 'Export Badges'}
          </button>
          <button
            onClick={() => fetchSubmissions()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Data
          </button>
        </div>
      </div>
      
      {currentEvent.auto_approve && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded border border-blue-200">
          <p>This event has auto-approval enabled. All submissions are automatically approved.</p>
        </div>
      )}
      
      {/* Simplified Search component */}
      <div className="mb-6 p-4 bg-white shadow rounded-lg">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2">
          <div className="flex-grow relative">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search by name, email, or phone number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search size={16} />
            </span>
          </div>
          <div className="flex space-x-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                fetchSubmissions({ search: '' });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Clear
            </button>
          </div>
        </form>
        <div className="mt-3 flex items-center gap-2">
          <label htmlFor="status-filter" className="text-sm text-gray-600">
            Status
          </label>
          <select
            id="status-filter"
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <SubmissionsDisplay 
        submissions={submissions} 
        refreshSubmissions={fetchSubmissions}
        onStatusChange={handleStatusChange}
        currentEvent={currentEvent}
      />

      {/* Import Modal */}
      <ImportSubmissionsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        currentEvent={currentEvent}
        onImportSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default SubmissionList;